import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ClipboardList } from "lucide-react";
import StaffShell from "../components/StaffShell";
import { api } from "../lib/api";

export default function Operations() {
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("");
  const [notes, setNotes] = useState({});
  const nav = useNavigate();

  const load = useCallback(() => {
    api(`/staff/requests${filter ? `?status=${filter}` : ""}`).then((d) => setRequests(d.requests)).catch(() => {});
  }, [filter]);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    await api(`/staff/requests/${id}`, { method: "PATCH", body: { status } });
    load();
  };

  const saveNotes = async (id) => {
    await api(`/staff/requests/${id}`, { method: "PATCH", body: { staff_notes: notes[id] } });
    load();
  };

  return (
    <StaffShell title="Customer requests">
      <div className="toolbar" data-testid="operations-filter">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="operations-status-filter">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
      </div>

      {requests === null && <div className="skeleton" style={{ height: 200 }} />}
      {requests && requests.length === 0 && (
        <div className="card muted" style={{ padding: 30, textAlign: "center" }}>No requests yet. New online requests appear here automatically.</div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {requests && requests.map((r) => {
          const d = r.data || {};
          return (
            <div className="card" key={r.request_number} style={{ padding: 22 }} data-testid={`request-card-${r.request_number}`}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <strong className="mono" style={{ fontSize: 15 }}>{r.request_number}</strong>
                  <span className={`pill ${r.status}`} style={{ marginLeft: 10 }} data-testid={`request-status-${r.request_number}`}>{r.status.replace("_", " ")}</span>
                </div>
                <span className="muted mono" style={{ fontSize: 12 }}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="grid-2" style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13.5, lineHeight: 1.8 }}>
                  <div><strong>{d.first_name} {d.last_name}</strong> · <a href={`tel:${d.phone}`} style={{ color: "var(--blue-bright)" }}>{d.phone}</a> · <span className="muted">{d.email}</span></div>
                  <div className="muted">{d.year} {d.make} {d.model}{d.vin ? ` · VIN ${d.vin}` : ""}{d.mileage ? ` · ${d.mileage} mi` : ""}</div>
                  <div style={{ marginTop: 6 }}><span className="pill">{d.service}</span></div>
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                  <p style={{ margin: 0 }}><span className="muted">Concern:</span> {d.concern}</p>
                  <p style={{ margin: "6px 0 0" }} className="muted">
                    Started: {d.problem_started} · Frequency: {d.frequency} · Start behavior: {d.starting_behavior} · Transport: {d.transport}
                  </p>
                  {(d.warnings || d.triggers || d.tests || d.accessories) && (
                    <p style={{ margin: "6px 0 0" }} className="muted">
                      {d.warnings && <>Warnings: {d.warnings}. </>}
                      {d.triggers && <>Triggers: {d.triggers}. </>}
                      {d.tests && <>Tests/parts: {d.tests}. </>}
                      {d.accessories && <>Accessories: {d.accessories}.</>}
                    </p>
                  )}
                  {(d.preferred_contact || d.date || d.preferred_time) && (
                    <p style={{ margin: "6px 0 0" }} className="muted">
                      Prefers: {d.preferred_contact}{d.date ? ` · date request ${d.date}` : ""}{d.preferred_time ? ` ${d.preferred_time}` : ""}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                {r.status === "new" && <>
                  <button className="btn btn-blue btn-sm" onClick={() => setStatus(r.request_number, "confirmed")} data-testid={`request-confirm-${r.request_number}`}><Check size={14} /> Confirm availability</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatus(r.request_number, "cancelled")} data-testid={`request-cancel-${r.request_number}`}><X size={14} /> Cancel</button>
                </>}
                {r.status === "confirmed" && (
                  <button className="btn btn-red btn-sm" onClick={() => nav(`/intake?request=${r.request_number}`)} data-testid={`request-checkin-${r.request_number}`}>
                    <ClipboardList size={14} /> Open store check-in
                  </button>
                )}
                <input placeholder="Private scheduling note…" value={notes[r.request_number] ?? r.staff_notes ?? ""}
                  onChange={(e) => setNotes({ ...notes, [r.request_number]: e.target.value })}
                  style={{ flex: 1, minWidth: 220, background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: 10, color: "var(--ink)", padding: "9px 12px", fontSize: 13 }}
                  data-testid={`request-notes-${r.request_number}`} />
                <button className="btn btn-ghost btn-sm" onClick={() => saveNotes(r.request_number)} data-testid={`request-save-notes-${r.request_number}`}>Save note</button>
              </div>
            </div>
          );
        })}
      </div>
    </StaffShell>
  );
}
